import { alpha, styled } from '@mui/material/styles';

import { rem, Theme } from '../../theme';

import { ButtonSize, Style } from './IconButton.types';

// Background color

export const buttonBackgroundColorMapOld = (
    theme: Theme,
): Record<Style, string> => ({
    primary: theme.palette.primary.main,
    'primary-inverted': theme.palette.common.white,
    secondary: theme.palette.grey[900],
    'secondary-inverted': 'transparent',
    'secondary-tonal': 'transparent',
});
export const buttonBackgroundColorMapNew = (
    theme: Theme,
): Record<Style, string> => ({
    primary: theme.palette.tgg.surface.action.main,
    'primary-inverted': theme.palette.tgg.surface.action.main,
    secondary: theme.palette.tgg.surface.action.secondary.main,
    'secondary-inverted': alpha(
        theme.palette.tgg.surface.action.secondaryInverse.main,
        0.4,
    ),
    'secondary-tonal': theme.palette.tgg.surface.action.secondaryTonal.main,
});

// Icon color

export const iconColorMapOld = (theme: Theme): Record<Style, string> => ({
    primary: theme.palette.common.white,
    'primary-inverted': theme.palette.primary.main,
    secondary: theme.palette.common.blue,
    'secondary-inverted': theme.palette.common.white,
    'secondary-tonal': theme.palette.common.blue,
});

export const iconColorMapNew = (
    theme: Theme,
    disabled?: boolean,
): Record<Style, string> => ({
    primary: disabled
        ? theme.palette.tgg.icon.action.disabled
        : theme.palette.tgg.icon.on.action.main,
    'primary-inverted': disabled
        ? theme.palette.tgg.icon.action.disabled
        : theme.palette.tgg.icon.on.action.main,
    secondary: disabled
        ? theme.palette.tgg.icon.action.disabled
        : theme.palette.tgg.icon.on.action.secondary,
    'secondary-inverted': disabled
        ? theme.palette.tgg.icon.action.inverse.disabled
        : theme.palette.tgg.icon.on.action.secondaryInverse,
    'secondary-tonal': disabled
        ? theme.palette.tgg.icon.action.disabled
        : theme.palette.tgg.icon.on.action.secondaryTonal,
});

// Border color

export const borderColorMapOld = (theme: Theme): Record<Style, string> => ({
    primary: theme.palette.common.white,
    'primary-inverted': theme.palette.common.blue,
    secondary: theme.palette.common.blue,
    'secondary-inverted': theme.palette.common.white,
    'secondary-tonal': theme.palette.common.white,
});

export const borderColorMapNew = (theme: Theme): Record<Style, string> => ({
    primary: 'transparent',
    'primary-inverted': 'transparent',
    secondary: theme.palette.tgg.border.action.main,
    'secondary-inverted': theme.palette.tgg.border.action.secondaryInverse,
    'secondary-tonal': 'transparent',
});

// Hovered background color

export const hoveredBackgroundColorMapOld = (
    theme: Theme,
): Record<Style, string> => ({
    primary: theme.palette.primary.main,
    'primary-inverted': theme.palette.common.white,
    secondary: theme.palette.grey[900],
    'secondary-inverted': 'transparent',
    'secondary-tonal': 'transparent',
});

export const hoveredBackgroundColorMapNew = (
    theme: Theme,
): Record<Style, string> => ({
    primary: theme.palette.tgg.surface.action.hover,
    'primary-inverted': 'transparent',
    secondary: theme.palette.tgg.surface.action.secondary.hover,
    'secondary-inverted':
        theme.palette.tgg.surface.action.secondaryInverse.hover,
    'secondary-tonal': theme.palette.tgg.surface.action.secondaryTonal.hover,
});

// Focused border color

export const focusedBorderColorMapOld = (
    theme: Theme,
): Record<Style, string> => ({
    primary: theme.palette.primary.main,
    'primary-inverted': theme.palette.primary.main,
    secondary: theme.palette.primary.main,
    'secondary-inverted': theme.palette.common.white,
    'secondary-tonal': theme.palette.primary.main,
});

export const focusedBorderColorMapNew = (
    theme: Theme,
): Record<Style, string> => ({
    primary: theme.palette.tgg.border.focus.main,
    'primary-inverted': 'transparent',
    secondary: theme.palette.tgg.border.focus.main,
    'secondary-inverted': theme.palette.tgg.border.focus.inverse,
    'secondary-tonal': theme.palette.tgg.border.focus.main,
});

// Pressed background color

export const pressedBackgroundColorMapOld = (
    theme: Theme,
): Record<Style, string> => ({
    primary: theme.palette.primary.main,
    'primary-inverted': theme.palette.common.white,
    secondary: theme.palette.grey[900],
    'secondary-inverted': 'transparent',
    'secondary-tonal': 'transparent',
});

export const pressedBackgroundColorMapNew = (
    theme: Theme,
): Record<Style, string> => ({
    primary: theme.palette.tgg.surface.action.pressed,
    'primary-inverted': 'transparent',
    secondary: theme.palette.tgg.surface.action.secondary.pressed,
    'secondary-inverted':
        theme.palette.tgg.surface.action.secondaryInverse.pressed,
    'secondary-tonal': theme.palette.tgg.surface.action.secondaryTonal.pressed,
});

// Disabled background color

export const disabledBackgroundColorMapOld = (
    theme: Theme,
): Record<Style, string> => ({
    primary: theme.palette.primary.main,
    'primary-inverted': theme.palette.common.white,
    secondary: theme.palette.grey[900],
    'secondary-inverted': 'transparent',
    'secondary-tonal': 'transparent',
});

export const disabledBackgroundColorMapNew = (
    theme: Theme,
): Record<Style, string> => ({
    primary: theme.palette.tgg.surface.action.disabled,
    'primary-inverted': 'transparent',
    secondary: theme.palette.tgg.surface.action.disabled,
    'secondary-inverted': theme.palette.tgg.surface.scrim,
    'secondary-tonal': theme.palette.tgg.surface.action.disabled,
});

// Disabled border color

export const disabledBorderColorMapOld = (
    theme: Theme,
): Record<Style, string> => ({
    primary: theme.palette.common.white,
    'primary-inverted': theme.palette.common.blue,
    secondary: theme.palette.common.blue,
    'secondary-inverted': theme.palette.common.white,
    'secondary-tonal': theme.palette.common.white,
});

export const disabledBorderColorMapNew = (
    theme: Theme,
): Record<Style, string> => ({
    primary: 'transparent',
    'primary-inverted': 'transparent',
    secondary: theme.palette.tgg.border.disabled,
    'secondary-inverted': theme.palette.tgg.border.inverse.disabled,
    'secondary-tonal': 'transparent',
});

export const StyledButton = styled('button')<{
    $style: Style;
    $size: ButtonSize;
}>`
    ${({
        theme,
        $style,
        $size,
    }: {
        $style: Style;
        theme: Theme;
        $size: ButtonSize;
    }) => `
        position: relative;
        width: ${
            $size === 'small'
                ? theme.tggSpacing(rem(3.2), 12)
                : theme.tggSpacing(rem(4), 12)
        };
        height: ${
            $size === 'small'
                ? theme.tggSpacing(rem(3.2), 12)
                : theme.tggSpacing(rem(4), 12)
        };
        display: ${theme.isRebranding('inline-block', 'flex')};
        justify-content: center;
        align-items: center;
        padding: ${theme.tggSpacing([rem(0.0625), rem(0.375)], 3)};
        cursor: pointer;
        background-color: ${theme.isRebranding(
            buttonBackgroundColorMapOld(theme)[$style],
            buttonBackgroundColorMapNew(theme)[$style],
        )};
        border: ${theme.tggSpacing(rem(0.2), 0.5)} solid ${theme.isRebranding(
            buttonBackgroundColorMapOld(theme)[$style],
            borderColorMapNew(theme)[$style],
        )};
        border-radius: 50%;
        &::before {
            position: absolute;
            top: ${theme.tggSpacing(rem(-0.2), -1)};
            left: ${theme.tggSpacing(rem(-0.2), -1)};
            display: none;
            width: ${
                $size === 'small'
                    ? theme.tggSpacing(rem(3.2), 12.8)
                    : theme.tggSpacing(rem(4), 16)
            };
            height: ${
                $size === 'small'
                    ? theme.tggSpacing(rem(3.2), 12.8)
                    : theme.tggSpacing(rem(4), 16)
            };
            content: '';
            border-radius: 50%;
        }

             &:hover {
            &::before {
                display: block;
                background-color: ${theme.getRebrandingColor(theme.palette.other.shadowGrey, 'none')};
            }
            background-color: ${theme.isRebranding(
                hoveredBackgroundColorMapOld(theme)[$style],
                hoveredBackgroundColorMapNew(theme)[$style],
            )};
        }

        &:active {
            &::before {
                display: block;
                background-color: ${theme.getRebrandingColor(theme.palette.other.buttonHover, 'none')};
            }
            background-color: ${theme.isRebranding(
                pressedBackgroundColorMapOld(theme)[$style],
                pressedBackgroundColorMapNew(theme)[$style],
            )};
        }

        &:focus {
            outline: ${theme.tggSpacing(rem(0.2), 0.5)} solid
                ${theme.isRebranding(
                    focusedBorderColorMapOld(theme)[$style],
                    focusedBorderColorMapNew(theme)[$style],
                )};
            outline-offset: ${theme.tggSpacing(rem(0.2), 0.5)};
        }

        &:disabled {
            opacity: ${theme.isRebranding('50%', '100%')};
            background-color: ${theme.isRebranding(
                disabledBackgroundColorMapOld(theme)[$style],
                disabledBackgroundColorMapNew(theme)[$style],
            )};
            border: ${theme.tggSpacing(0, 0.5)} solid
                ${theme.isRebranding(
                    disabledBorderColorMapOld(theme)[$style],
                    disabledBorderColorMapNew(theme)[$style],
                )};
        }
    `}
`;
